import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { PaperPlaneTilt, X, Plus } from "@phosphor-icons/react";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const KINDS = {
  invoice: { label: "la facture", url: (d) => `/invoices/${d.id}/send`, number: (d) => d.invoice_number },
  quote: { label: "la soumission", url: (d) => `/quotes/${d.id}/send`, number: (d) => d.quote_number },
  contract: { label: "le contrat", url: (d) => `/contracts/${d.id}/send`, number: (d) => d.contract_number },
};

export default function SendDialog({ target, onOpenChange, onSent }) {
  const [to, setTo] = useState([]);
  const [suggested, setSuggested] = useState([]);
  const [draft, setDraft] = useState("");
  const [message, setMessage] = useState("");
  const [remember, setRemember] = useState(true);
  const [sending, setSending] = useState(false);
  const kind = target ? KINDS[target.kind] : null;
  const doc = target?.doc;

  useEffect(() => {
    if (!target) return;
    const primary = (doc.client_info?.email || doc.client?.email || "").toLowerCase();
    setTo(primary ? [primary] : []); setDraft(""); setMessage(""); setRemember(true);
    api.get("/clients").then((r) => {
      const cl = r.data.find((c) => c.id === doc.client_id);
      setSuggested((cl?.cc_emails || []).map((e) => e.toLowerCase()).filter((e) => e !== primary));
    }).catch(() => setSuggested([]));
  }, [target]); // eslint-disable-line react-hooks/exhaustive-deps

  const add = (raw) => {
    const parts = (raw || draft).split(/[,;\s]+/).map((s) => s.trim().toLowerCase()).filter(Boolean);
    if (!parts.length) return;
    const bad = parts.filter((p) => !EMAIL_RE.test(p));
    if (bad.length) { toast.error(`Courriel invalide : ${bad[0]}`); return; }
    setTo((prev) => [...new Set([...prev, ...parts])]); setDraft("");
  };
  const remove = (e) => setTo(to.filter((x) => x !== e));
  const onKey = (e) => { if (["Enter", ",", ";", "Tab"].includes(e.key) && draft.trim()) { e.preventDefault(); add(); } };

  const send = async () => {
    const list = draft.trim() ? [...new Set([...to, draft.trim().toLowerCase()])] : to;
    if (draft.trim() && !EMAIL_RE.test(draft.trim())) { toast.error(`Courriel invalide : ${draft.trim()}`); return; }
    if (!list.length) { toast.error("Ajoutez au moins un destinataire."); return; }
    setSending(true);
    toast.loading("Envoi en cours…", { id: "send" });
    try {
      const { data } = await api.post(kind.url(doc), { recipients: list, cc: [], message, remember });
      toast.success(`Envoyé à ${(data.sent_to || list).join(", ")}`, { id: "send" });
      onOpenChange(false); onSent?.();
    } catch (e) { toast.error(apiError(e), { id: "send" }); }
    finally { setSending(false); }
  };

  return (
    <Dialog open={!!target} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-lg" data-testid="send-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><PaperPlaneTilt size={20} /> Envoyer {kind?.label} {doc && kind.number(doc)}</DialogTitle>
          <DialogDescription>Le PDF sera joint au courriel. Ajoutez autant de destinataires que nécessaire (Entrée ou virgule pour ajouter).</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Destinataires</Label>
            <div className="flex flex-wrap gap-1.5 border border-input rounded-sm p-2 min-h-[44px] bg-background" data-testid="recipients-box">
              {to.map((e) => (
                <span key={e} data-testid={`recipient-chip-${e}`} className="inline-flex items-center gap-1 rounded-sm bg-primary/10 text-primary border border-primary/30 px-2 py-0.5 text-sm">
                  {e}<button type="button" data-testid={`remove-recipient-${e}`} onClick={() => remove(e)} className="hover:text-destructive"><X size={12} /></button>
                </span>
              ))}
              <input data-testid="recipient-input" className="flex-1 min-w-[180px] bg-transparent outline-none text-sm px-1" placeholder={to.length ? "Ajouter un courriel…" : "courriel@exemple.com"} value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={onKey} onBlur={() => draft.trim() && add()} />
            </div>
          </div>
          {suggested.filter((s) => !to.includes(s)).length > 0 && (
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Contacts connus de ce client</Label>
              <div className="flex flex-wrap gap-1.5">
                {suggested.filter((s) => !to.includes(s)).map((s) => (
                  <button key={s} type="button" data-testid={`suggest-${s}`} onClick={() => setTo([...to, s])} className="inline-flex items-center gap-1 rounded-sm border border-border px-2 py-0.5 text-sm hover:bg-muted"><Plus size={12} /> {s}</button>
                ))}
              </div>
            </div>
          )}
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Message personnel (optionnel)</Label>
            <Textarea data-testid="send-message" className="rounded-sm" rows={3} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Ajouté au corps du courriel…" />
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox data-testid="send-remember" checked={remember} onCheckedChange={(v) => setRemember(!!v)} /> Mémoriser les nouvelles adresses comme contacts de ce client
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button data-testid="send-confirm-btn" className="rounded-sm" onClick={send} disabled={sending}><PaperPlaneTilt size={16} className="mr-2" /> {sending ? "Envoi…" : `Envoyer (${to.length + (draft.trim() ? 1 : 0)})`}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
